import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import ReactMarkdown from "react-markdown";

const supabase = createClient(
  "https://jfuxxeegqerhuhgxzlio.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmdXh4ZWVncWVyaHVoZ3h6bGlvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM3OTI2NjAsImV4cCI6MjA3OTM2ODY2MH0.ur266CqIa5Mht67cbZQhOa2yE09K75cBpPyzML8AKsw"
);

interface FaqData {
  id: number;
  category: string;
  question: string;
  answer: string;
}

interface NoticeData {
  id: number;
  category: string;
  title: string;
  content: string;
  created_at: string;
}

function HomePage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");
  const [faqs, setFaqs] = useState<FaqData[]>([]);
  const [notices, setNotices] = useState<NoticeData[]>([]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // 모달 열릴 때 스크롤 방지
  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [modalOpen]);

  // FAQ와 공지사항 데이터 불러오기
  useEffect(() => {
    const fetchData = async () => {
      // FAQ 가져오기 (최신 4개)
      const { data: faqData } = await supabase
        .from("faqs")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true })
        .limit(4);
      if (faqData) setFaqs(faqData);

      // 공지사항 가져오기 (최신 3개)
      const { data: noticeData } = await supabase
        .from("notices")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false })
        .limit(3);
      if (noticeData) setNotices(noticeData);
    };
    fetchData();
  }, []);

  // FAQ 상세 모달 열기
  const openFaqModal = (faq: FaqData) => {
    setModalTitle(faq.question);
    setModalContent(faq.answer);
    setModalOpen(true);
  };

  // 공지사항 상세 모달 열기
  const openNoticeModal = (notice: NoticeData) => {
    setModalTitle(notice.title);
    setModalContent(notice.content);
    setModalOpen(true);
  };

  // 날짜 포맷
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\. /g, ".")
      .replace(/\.$/, "");
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans">
      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-800">{modalTitle}</h2>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              <div className="prose prose-sm max-w-none text-slate-700 prose-headings:text-slate-800 prose-headings:font-bold prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-p:my-2 prose-ul:my-2 prose-li:my-0">
                <ReactMarkdown>
                  {modalContent.replace(/\\n/g, "\n")}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-gradient-to-r from-[#9b5de5] to-[#f15bb5] shadow-md">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-24">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <div className="w-8 h-8 rounded-full bg-white/20 border border-white/30 shadow-lg backdrop-blur-sm flex items-center justify-center">
                <img
                  src="/images/ic_header_logo.svg"
                  alt="LOVATA"
                  className="w-5 h-5 brightness-0 invert"
                />
              </div>
              <span className="text-xl font-extrabold text-white tracking-wide">
                LOVATA
              </span>
            </div>

            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection("faq")}
                className="text-sm font-medium transition-colors hover:text-white/80 text-white/90"
              >
                FAQ
              </button>
              <button
                onClick={() => scrollToSection("notice")}
                className="text-sm font-medium transition-colors hover:text-white/80 text-white/90"
              >
                공지사항
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-sm font-medium transition-colors hover:text-white/80 text-white/90"
              >
                1:1 문의
              </button>
            </nav>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button className="h-8 rounded-md px-3 text-white hover:bg-white/20 text-sm font-medium transition-all">
              로그인
            </button>
          </div>

          <button className="md:hidden text-white hover:bg-white/20 p-2 rounded-md">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6"
            >
              <path d="M4 5h16"></path>
              <path d="M4 12h16"></path>
              <path d="M4 19h16"></path>
            </svg>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="flex flex-col gap-12 pb-12 bg-slate-50">
          {/* Hero Section */}
          <section className="container mx-auto px-4 py-8 relative z-20">
            <div className="bg-white/80 backdrop-blur-md rounded-[2.5rem] p-8 md:p-12 shadow-2xl shadow-purple-500/10 border border-white/50 overflow-hidden relative">
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#f15bb5]/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-[#9b5de5]/10 rounded-full blur-3xl"></div>

              <div className="grid md:grid-cols-2 gap-12 items-center relative z-10">
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 text-[#9b5de5] font-bold text-sm mb-6">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#f15bb5] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#f15bb5]"></span>
                    </span>
                    NEW LOVATA APP
                  </div>

                  <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                    더 새로워진
                    <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9b5de5] to-[#f15bb5]">
                      LOVATA
                    </span>
                    를 만나보세요
                  </h2>

                  <p className="text-slate-600 text-lg mb-8 leading-relaxed max-w-lg">
                    취향 기반 소셜 커뮤니티 LOVATA
                    <br />
                    콘텐츠와 관심사를 중심으로 연결되는 프라이빗 커뮤니티 플랫폼
                    <br />
                    인증된 사용자와 관심 주제로 소통해보세요.
                  </p>

                  <p className="text-slate-500 text-sm mb-4 font-medium">
                    모바일 기기에서는 각 스토어에서 설치할 수 있습니다.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Google Play Button */}
                    <button className="flex items-center justify-center bg-black text-white px-3 py-1.5 rounded-lg hover:opacity-80 transition-opacity border border-white/10 h-[48px] flex-1 sm:flex-none sm:min-w-[150px]">
                      <div className="w-8 h-8 mr-2 flex items-center justify-center">
                        <svg
                          viewBox="0 0 24 24"
                          className="w-7 h-7"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.012,3.71c-0.128,0.138-0.21,0.347-0.21,0.615v15.349c0,0.268,0.082,0.477,0.21,0.615l0.05,0.041L12.56,11.841 v-0.194L4.062,3.67L4.012,3.71z"
                            fill="#2196F3"
                          ></path>
                          <path
                            d="M15.424,14.706l-2.863-2.864v-0.194l2.863-2.864l0.051,0.031l3.391,1.926c0.968,0.55,0.968,1.448,0,1.998 l-3.391,1.926L15.424,14.706z"
                            fill="#FFC107"
                          ></path>
                          <path
                            d="M12.871,11.841l-8.809,8.489c0.237,0.252,0.627,0.283,1.066,0.031l10.296-5.849L12.871,11.841z"
                            fill="#4CAF50"
                          ></path>
                          <path
                            d="M12.871,11.647L15.424,9.112L5.128,3.264c-0.439-0.252-0.829-0.221-1.066,0.031L12.871,11.647z"
                            fill="#F44336"
                          ></path>
                        </svg>
                      </div>
                      <div className="flex flex-col items-start justify-center">
                        <span className="text-[9px] uppercase leading-none mb-[2px]">
                          GET IT ON
                        </span>
                        <span className="text-[15px] font-semibold leading-none font-sans tracking-wide">
                          Google Play
                        </span>
                      </div>
                    </button>

                    {/* App Store Button */}
                    <button className="flex items-center justify-center bg-black text-white px-3 py-1.5 rounded-lg hover:opacity-80 transition-opacity border border-white/10 h-[48px] flex-1 sm:flex-none sm:min-w-[150px]">
                      <div className="w-8 h-8 mr-2 flex items-center justify-center">
                        <svg
                          viewBox="0 0 384 512"
                          className="w-6 h-6 fill-white"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"></path>
                        </svg>
                      </div>
                      <div className="flex flex-col items-start justify-center">
                        <span className="text-[9px] leading-none mb-[2px]">
                          Download on the
                        </span>
                        <span className="text-[15px] font-semibold leading-none font-sans tracking-wide">
                          App Store
                        </span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Phone Mockups */}
                <div className="relative h-[400px] md:h-[500px] hidden md:flex justify-center items-center">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-gradient-to-tr from-[#9b5de5]/20 to-[#f15bb5]/20 rounded-full blur-[60px]"></div>
                  <div className="relative flex gap-6 -rotate-6 hover:rotate-0 transition-transform duration-700 ease-out cursor-pointer group">
                    <div className="w-[260px] rounded-[2.5rem] overflow-hidden shadow-2xl shadow-purple-900/20 border-[6px] border-white relative z-10 translate-y-8 group-hover:translate-y-6 transition-transform duration-500">
                      <img
                        src="/images/main00.png"
                        alt="LOVATA Home"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="w-[260px] rounded-[2.5rem] overflow-hidden shadow-2xl shadow-purple-900/20 border-[6px] border-white relative z-0 -translate-y-8 group-hover:-translate-y-6 hidden sm:block transition-transform duration-500">
                      <img
                        src="/images/main01.png"
                        alt="LOVATA Profile"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ & Notice Section */}
          <section className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12">
              {/* FAQ */}
              <div
                id="faq"
                className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 scroll-mt-20"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-800">
                    자주 묻는 질문
                  </h2>
                  <button className="flex items-center text-slate-500 hover:text-[#9b5de5] text-sm font-medium transition-colors">
                    더보기
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 ml-1"
                    >
                      <path d="m9 18 6-6-6-6"></path>
                    </svg>
                  </button>
                </div>

                <div className="space-y-3">
                  {faqs.length > 0 ? (
                    faqs.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => openFaqModal(item)}
                        className="p-4 bg-slate-50 rounded-2xl hover:bg-purple-50 transition-all cursor-pointer flex justify-between items-center group"
                      >
                        <div>
                          <span className="text-xs font-bold text-[#9b5de5] mb-1 block">
                            {item.category}
                          </span>
                          <p className="font-bold text-slate-700 group-hover:text-[#9b5de5] transition-colors">
                            {item.question}
                          </p>
                        </div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-5 h-5 text-slate-300 group-hover:text-[#9b5de5] transition-colors"
                        >
                          <path d="m9 18 6-6-6-6"></path>
                        </svg>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      등록된 FAQ가 없습니다.
                    </div>
                  )}
                </div>
              </div>

              {/* Notice */}
              <div
                id="notice"
                className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 scroll-mt-20"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-800">
                    공지사항
                  </h2>
                  <button className="flex items-center text-slate-500 hover:text-[#9b5de5] text-sm font-medium transition-colors">
                    더보기
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 ml-1"
                    >
                      <path d="m9 18 6-6-6-6"></path>
                    </svg>
                  </button>
                </div>

                <div className="space-y-3">
                  {notices.length > 0 ? (
                    notices.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => openNoticeModal(item)}
                        className="p-4 bg-slate-50 rounded-2xl hover:bg-purple-50 transition-all cursor-pointer flex justify-between items-center group"
                      >
                        <div className="flex-1 min-w-0 mr-4">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-[#9b5de5]/10 text-[#9b5de5] text-[10px] rounded-full font-bold">
                              {item.category || "NOTICE"}
                            </span>
                            <span className="text-xs text-slate-400">
                              {formatDate(item.created_at)}
                            </span>
                          </div>
                          <p className="font-bold text-slate-700 truncate group-hover:text-[#9b5de5] transition-colors">
                            {item.title}
                          </p>
                        </div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-5 h-5 text-slate-300 group-hover:text-[#9b5de5] transition-colors"
                        >
                          <path d="m9 18 6-6-6-6"></path>
                        </svg>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      등록된 공지사항이 없습니다.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section id="contact" className="container mx-auto px-4 scroll-mt-20">
            <div className="bg-gradient-to-r from-[#9b5de5] to-[#f15bb5] rounded-3xl p-8 md:p-16 text-center text-white shadow-xl shadow-purple-200 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 relative z-10">
                원하는 답변을 찾지 못하셨나요?
              </h2>
              <p className="text-white/90 mb-8 relative z-10">
                1:1 문의를 남겨주시면 신속하게 답변 드리겠습니다.
              </p>
              <button className="bg-white text-[#9b5de5] hover:bg-white/90 font-bold h-14 px-8 rounded-full shadow-lg relative z-10 text-sm transition-all">
                1:1 문의하기
              </button>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-lg font-bold text-white">LOVATA</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                언제 어디서나 당신의 일상을
                <br />
                더욱 특별하게 만들어드립니다.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">정책 및 약관</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    이용약관
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    개인정보처리방침
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="mt-2">help.lovata@gmail.com</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-xs text-slate-500">
            <div className="flex flex-col gap-2 mb-4">
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                <span>
                  <b className="font-medium text-slate-400">대표자</b> 김규태
                </span>
                <span className="hidden sm:inline text-slate-700">|</span>
                <span>
                  <b className="font-medium text-slate-400">사업자등록번호</b>{" "}
                  847-72-00560
                </span>
                <span className="hidden sm:inline text-slate-700">|</span>
                <span>
                  <b className="font-medium text-slate-400">주소</b> 경기도
                  부천시 원미구 부천로 130번길 80 401호
                </span>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                <span>
                  <b className="font-medium text-slate-400">통신판매신고번호</b>{" "}
                  2025-부천원미-2468
                </span>
                <span className="hidden sm:inline text-slate-700">|</span>
                <span>
                  <b className="font-medium text-slate-400">전화번호</b>{" "}
                  010-3247-6727
                </span>
                <span className="hidden sm:inline text-slate-700">|</span>
                <span>
                  <b className="font-medium text-slate-400">이메일</b>{" "}
                  help.lovata@gmail.com
                </span>
              </div>
            </div>
            <p>© 2025 LOVATA Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
